import React from 'react';
import { Box, Typography } from '@mui/material';

const initialsOf = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
};

/**
 * A brand's logo, or an explicit "NO LOGO" stand-in when it has none.
 *
 * The logo is optional at every point in a brand's life — it is not asked for
 * at creation and may never be set — so the empty state is the normal state,
 * not an error. Saying "NO LOGO" is better than an anonymous grey circle: it
 * tells the owner something is missing and that it was not a failed load.
 *
 * Below `initialsBelow` px the words do not fit legibly, so small instances
 * (menu rows, table cells, 28px avatars) fall back to the brand's initials —
 * which read as a deliberate monogram at that size, where shrunken "NO LOGO"
 * would just be grey mush.
 */
export default function BrandLogo({
  src,
  name = '',
  size = 64,
  rounded = true,
  initialsBelow = 44,
  sx,
}) {
  const shared = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: rounded ? '50%' : '12px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...sx,
  };

  if (src) {
    return (
      <Box
        component='img'
        src={src}
        alt={name ? `${name} logo` : 'Brand logo'}
        sx={{ ...shared, objectFit: 'cover', bgcolor: 'background.paper' }}
      />
    );
  }

  const initials = initialsOf(name);
  const useInitials = size < initialsBelow && initials;

  return (
    <Box
      // Read out as the same thing sighted users see.
      aria-label={name ? `${name} — no logo` : 'No logo'}
      role='img'
      sx={{
        ...shared,
        bgcolor: useInitials ? 'primary.main' : 'action.hover',
        border: useInitials ? 'none' : '1px dashed',
        borderColor: 'divider',
        color: useInitials ? '#fff' : 'text.secondary',
      }}
    >
      {useInitials ? (
        <Typography component='span' fontWeight='700' sx={{ fontSize: size * 0.38 }}>
          {initials}
        </Typography>
      ) : (
        <Typography
          component='span'
          fontWeight='700'
          textAlign='center'
          sx={{
            // Scales with the box so one component works at 48px and 160px.
            fontSize: Math.max(9, Math.min(13, size * 0.17)),
            lineHeight: 1.15,
            letterSpacing: '0.4px',
            px: '4px',
          }}
        >
          NO<br />LOGO
        </Typography>
      )}
    </Box>
  );
}
